import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';

import { ImageField, TextField } from 'common/forms/Field';
import { FormFooter } from 'common/forms/components/FormFooter';
import { FormSection } from 'common/components/FormSection';
import { Banner } from 'common/components/Banner';
import { Img } from 'common/components/Img';
import { Link } from 'common/components/Link';
import { CARD_IMAGE_MAX_SIZE, CARD_IMAGE_TYPE } from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import { ILocationFormValues } from 'organisation/types';
import { getLocationFormValidator } from 'organisation/validators/locationForm';
import LocationCardImage from 'assets/images/module-card-image.webp';

interface IProps {
    initialValues: ILocationFormValues;
    isEdit?: boolean;
    onSubmit: SubmitHandler<ILocationFormValues>;
    onClose?(): void;
    onDelete?(): void | Promise<void>;
}

export const LocationForm = ({
    initialValues,
    isEdit,
    onSubmit,
    onClose,
    onDelete,
}: IProps) => {
    const [translate] = useTranslation();

    const formMethods = useForm<ILocationFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(getLocationFormValidator()),
    });

    const { control, handleSubmit } = formMethods;

    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );

    return (
        <FormProvider {...formMethods}>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography variant="h3">
                        {translate(
                            isEdit
                                ? 'locationsManagementPage.editLocation'
                                : 'locationsManagementPage.newLocation'
                        )}
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <TextField
                            control={control}
                            label={translate('title')}
                            name="title"
                        />

                        <TextField
                            control={control}
                            label={translate('locationsManagementPage.address')}
                            name="address"
                        />

                        <TextField
                            multiline
                            control={control}
                            label={translate('description')}
                            name="description"
                        />

                        <FormSection
                            description={
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                rel="noreferrer"
                                                underline="always"
                                            />
                                        ),
                                    }}
                                    i18nKey="locationsManagementPage.imageDescription"
                                />
                            }
                            title={translate('image')}
                        >
                            <Stack
                                direction={
                                    smallScreen ? 'column-reverse' : 'row'
                                }
                                spacing={2}
                                sx={{ mt: 2 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <ImageField
                                        acceptedFileTypes={CARD_IMAGE_TYPE}
                                        control={control}
                                        helperText={translate(
                                            'imageSizeDescription',
                                            {
                                                width: IMAGE_VARIANTS[
                                                    EImageVariant.Card
                                                ].minWidth,
                                                height: IMAGE_VARIANTS[
                                                    EImageVariant.Card
                                                ].minHeight,
                                            }
                                        )}
                                        imageVariant={EImageVariant.Card}
                                        label={translate('image')}
                                        maxUploadSize={CARD_IMAGE_MAX_SIZE}
                                        name="image"
                                    />
                                </Box>
                                <Box sx={{ width: '100%' }}>
                                    <Banner>
                                        <Box sx={{ py: 1, pr: 1, pl: 2 }}>
                                            {translate(
                                                'locationsManagementPage.imageBannerDescription'
                                            )}
                                        </Box>
                                        <Box sx={{ mt: 'auto' }}>
                                            <Img src={LocationCardImage} />
                                        </Box>
                                    </Banner>
                                </Box>
                            </Stack>
                        </FormSection>

                        <FormFooter
                            canDelete={!!onDelete}
                            deleteDialogText={translate(
                                'locationsManagementPage.locationsDeleteDialog.text',
                                { count: 1 }
                            )}
                            deleteDialogTitle={translate(
                                'locationsManagementPage.locationsDeleteDialog.title',
                                { count: 1 }
                            )}
                            onClose={onClose}
                            onDelete={onDelete}
                        />
                    </Box>
                </form>
            </Box>
        </FormProvider>
    );
};
